/* eslint-disable no-console */
import { LitElement, html, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { Editor } from '@tiptap/core';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Document } from '@tiptap/extension-document';
import { Text } from '@tiptap/extension-text';
import { BulletList } from '@tiptap/extension-bullet-list';
import { Blockquote } from '@tiptap/extension-blockquote';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Code } from '@tiptap/extension-code';
import { CodeBlock } from '@tiptap/extension-code-block';
import { Dropcursor } from '@tiptap/extension-dropcursor';
import { Gapcursor } from '@tiptap/extension-gapcursor';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Heading } from '@tiptap/extension-heading';
import { ListItem } from '@tiptap/extension-list-item';
import { OrderedList } from '@tiptap/extension-ordered-list';
import { Strike } from '@tiptap/extension-strike';
import { SpellCheckerService } from './services/SpellCheckerService.js';
import { UsageTrackingService } from './services/UsageTrackingService.js';
import { SpellingErrorMark } from './marks/SpellingErrorMark.js';
import { GrammarErrorMark } from './marks/GrammarErrorMark.js';
import { styles } from './styles/spell-checker-styles.js';
import { TextError } from './types/TextError.js';
import 'sg-feedback-wc/build/';

interface AuthContext {
  isLogged: boolean;
  isPremium: boolean;
}

// Extend HTMLElement to include our custom property
declare global {
  interface HTMLElement {
    authContext?: AuthContext;
  }
}

export class SgSpellChecker extends LitElement {
  static styles = styles;

  @property({ type: Object })
  errorStyles = {
    spellingError: '#ff4444',
    grammarError: '#4444ff',
  };

  @property({ type: String })
  language = 'pt-BR';

  @property({ type: String })
  text = '';

  @state()
  private errors: TextError[] = [];

  @state()
  private isLoading = false;

  @state()
  private activeHoverCard: {
    x: number;
    y: number;
    error: TextError;
  } | null = null;

  @state()
  private showCopyFeedback = false;

  @state()
  private isEditorEmpty = true;

  @state()
  private isRewriting = false;

  @state()
  private originalText = '';

  @state()
  private rewrittenText = '';

  @state()
  private showDiff = false;

  @state()
  private showDiffView = false;

  @state()
  private showingOriginal = false;

  @state()
  private hasChecked = false;

  @state()
  private charCount = 0;

  @state()
  private showCountWarning = false;

  @state()
  private showEditorWarning = false;

  @state()
  private renderRewriteButton = false;

  @state()
  private lastCheckedText = '';

  @state()
  private showUsageLimitError = false;

  @state()
  private isVisible = false;

  @state()
  private preferAi = true;

  @state()
  private isLogged = false;

  @state()
  private isPremium = false;

  @property({ type: Boolean })
  showSidebar = false;

  @property({ type: String })
  endpointFeedback = '';

  @state()
  private hasCheckedWithNoErrors = false;

  private static readonly MAX_CHARS = 1000;

  private spellCheckerService = new SpellCheckerService();

  private editor?: Editor;

  private observer?: IntersectionObserver;

  private authContextListener = ((e: Event) => {
    const customEvent = e as CustomEvent<{
      isLogged: boolean;
      isPremium: boolean;
    }>;
    this.isLogged = customEvent.detail.isLogged;
    this.isPremium = Boolean(customEvent.detail.isPremium);
  }) as (e: Event) => void;

  firstUpdated() {
    // Check usage when component loads
    if (!this.isPremium) {
      const hasAvailableUsage = UsageTrackingService.hasAvailableUsage();
      if (!hasAvailableUsage) {
        this.showUsageLimitError = true;
        setTimeout(() => {
          this.showUsageLimitError = false;
        }, 3000);
      }
    }

    const editorElement = this.shadowRoot?.querySelector('#editor');
    if (editorElement) {
      // Desabilita correção ortográfica nativa
      editorElement.setAttribute('spellcheck', 'false');
      editorElement.setAttribute('autocorrect', 'off');
      editorElement.setAttribute('autocapitalize', 'off');
      editorElement.setAttribute('data-gramm', 'false'); // Desabilita Grammarly
      editorElement.setAttribute('data-gramm_editor', 'false'); // Desabilita Grammarly
      editorElement.setAttribute('data-enable-grammarly', 'false'); // Desabilita Grammarly

      this.editor = new Editor({
        element: editorElement as HTMLElement,
        extensions: [
          SpellingErrorMark,
          GrammarErrorMark,
          Paragraph,
          Document,
          Text,
          BulletList,
          Blockquote,
          Bold,
          Italic,
          Code,
          CodeBlock,
          Dropcursor,
          Gapcursor,
          HardBreak,
          Heading,
          ListItem,
          OrderedList,
          Strike,
        ],
        content: `<p>${this.text}</p>`,
        editorProps: {
          attributes: {
            spellcheck: 'false',
            autocorrect: 'off',
            autocapitalize: 'off',
            'data-gramm': 'false',
            'data-gramm_editor': 'false',
            'data-enable-grammarly': 'false',
          },
          handlePaste: () => true,
        },
        onUpdate: ({ editor }) => {
          this.text = editor.getText();
          this.isEditorEmpty = editor.isEmpty;
          this.charCount = editor.getText().length;
        },
      });

      // Initialize character count
      this.charCount = this.editor.getText().length;

      // Add click event listener
      editorElement.addEventListener('click', (e: Event) => {
        this._handleEditorClick(e as MouseEvent);
      });

      // Add paste event listener for Ctrl+V and right-click paste
      editorElement.addEventListener('paste', ((e: Event) => {
        this._handlePaste(e as ClipboardEvent);
      }) as (e: Event) => void);
    }

    // Setup Intersection Observer for the editor element
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting;
          this.requestUpdate();
        });
      },
      { threshold: 0.1 },
    );

    // Observe the editor element if it exists
    if (editorElement) {
      this.observer.observe(editorElement);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.authContext) {
      this.isLogged = this.authContext.isLogged;
      this.isPremium = Boolean(this.authContext.isPremium);
    }
    this.addEventListener('auth-context-changed', this.authContextListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('auth-context-changed', this.authContextListener);
    this.editor?.destroy();
    this.observer?.disconnect();
  }

  render() {
    const currentText = this.editor?.getText() || '';
    const isUnchangedText = currentText === this.lastCheckedText;
    const hasErrors = this.errors.length > 0;

    return html`
      <div class="editor-container">
        ${this.showUsageLimitError
          ? html`
              <div class="error-message">
                Você atingiu o limite de 3 verificações por dia. Tente novamente
                amanhã.
              </div>
            `
          : nothing}
        <div class="container ${!this.showSidebar ? 'no-sidebar' : ''}">
          <div class="editor-section">
            <div
              class="editor-wrapper ${this.showEditorWarning
                ? 'warning-pulse'
                : ''}"
            >
              ${this.isEditorEmpty
                ? html`
                    <button
                      class="paste-area"
                      @click=${this._pasteText}
                      @keydown=${this._pasteText}
                      title="Colar texto"
                    >
                      <svg viewBox="0 0 24 24">
                        <path
                          d="M19 2h-4.18C14.4.84 13.3 0 12 0c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm7 18H5V4h2v3h10V4h2v16z"
                        />
                      </svg>
                      <span class="paste-text">Clique para colar texto</span>
                      <span class="paste-shortcut">Ctrl+V</span>
                    </button>
                  `
                : html`
                    <div class="editor-actions">
                      <button
                        class="icon-button"
                        @click=${this._clearText}
                        title="Limpar texto"
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                          />
                        </svg>
                      </button>
                      <button
                        class="icon-button"
                        @click=${this._copyText}
                        title="Copiar texto"
                      >
                        <svg viewBox="0 0 24 24">
                          <path
                            d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"
                          />
                        </svg>
                      </button>
                    </div>
                  `}
              <div
                id="editor"
                class="content-editable"
                spellcheck="false"
              ></div>
              <div class="counters">
                ${!this.isPremium ? this._renderCharCount() : nothing}
                ${this.errors.length ? this._renderErrorCounts() : ''}
              </div>
            </div>
            <div class="editor-controls">
              <div class="main-controls">
                <button
                  class="check-button ${this.isVisible
                    ? 'check-button-onscreen'
                    : ''} ${!hasErrors ? 'check-button-has-errors' : ''}"
                  @click=${() => this._checkText(false)}
                  ?disabled=${this.isLoading || isUnchangedText}
                >
                  ${this.isLoading
                    ? html`<span class="spinner"></span>Verificando...`
                    : 'Verificar Texto'}
                </button>
              </div>
              ${!this.showSidebar && this.errors.length > 0
                ? html` <button
                    class="apply-all-button ${this.isVisible
                      ? 'apply-all-button-onscreen'
                      : ''}"
                    @click=${this._applyAllCorrections}
                  >
                    Aplicar Todas as Correções
                  </button>`
                : nothing}
              ${this.renderRewriteButton
                ? html`
                    <button
                      class="rewrite-button"
                      @click=${this._rewriteText}
                      ?disabled=${this.isRewriting || this.isEditorEmpty}
                    >
                      ${this.isRewriting
                        ? html`<span class="spinner"></span>Reescrevendo...`
                        : 'Corrigir texto com IA'}
                    </button>
                  `
                : nothing}
              <sg-feedback
                endpoint=${this.endpointFeedback}
                contentText=${this.text}
                contentTitle="Corrigir Textos"
                contentUrl="/"
                contentId="1"
                lang="pt-BR"
                projectId="11"
                contentType="corrector"
                projectName="Corrigir Textos"
              ></sg-feedback>
            </div>
          </div>
          ${this.showSidebar
            ? html`<div class="sidebar">${this._renderErrorDetails()}</div>`
            : nothing}
        </div>

        ${this.showDiff ? this._renderDiff() : ''}
        ${this.activeHoverCard ? this._renderHoverCard() : ''}
        ${this.showCopyFeedback
          ? html`<div class="copy-feedback">Texto copiado!</div>`
          : ''}
      </div>
    `;
  }

  private _renderErrorDetails() {
    if (this.isLoading) {
      return html`
        <div class="no-errors-container loading">
          <svg class="loading-spinner" viewBox="0 0 24 24">
            <path
              d="M12 6v3l4-4-4-4v3c-4.42 0-8 3.58-8 8 0 1.57.46 3.03 1.24 4.26L6.7 14.8c-.45-.83-.7-1.79-.7-2.8 0-3.31 2.69-6 6-6zm6.76 1.74L17.3 9.2c.44.84.7 1.79.7 2.8 0 3.31-2.69 6-6 6v-3l-4 4 4 4v-3c4.42 0 8-3.58 8-8 0-1.57-.46-3.03-1.24-4.26z"
            />
          </svg>
          <span>Verificando texto...</span>
          <p>Aguarde enquanto analisamos seu texto</p>
        </div>
      `;
    }

    if (!this.hasChecked) {
      return html`
        <div class="no-errors-container">
          <svg viewBox="0 0 24 24">
            <path
              d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
            />
          </svg>
          <span>Nada para verificar ainda!</span>
          <p>Comece adicionando texto ao editor</p>
        </div>
      `;
    }

    if (!this.errors.length && !this.isLoading) {
      return html`
        <div class="no-errors-container">
          <svg viewBox="0 0 24 24">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
          <span>Nenhum erro encontrado!</span>
          <p>O texto está gramaticalmente correto.</p>
        </div>
      `;
    }

    return html`
      <div class="error-details-container">
        ${this.errors.length > 0
          ? html`
              <button
                class="apply-all-button ${this.isVisible
                  ? 'apply-all-button-onscreen'
                  : ''}"
                @click=${this._applyAllCorrections}
              >
                Aplicar Todas as Correções
              </button>
            `
          : nothing}
        ${this.errors.map(
          error => html`
            <div class="error-detail">
              <div class="error-header">
                <h3>
                  ${error.type === 'spelling'
                    ? html`<svg class="error-icon" viewBox="0 0 24 24">
                          <path
                            d="M12.45 16h2.09L9.43 3H7.57L2.46 16h2.09l1.12-3h5.64l1.14 3zm-6.02-5L8.5 5.48 10.57 11H6.43zm15.16.59l-8.09 8.09L9.83 16l-1.41 1.41 5.09 5.09L23 13l-1.41-1.41z"
                          />
                        </svg>
                        Erro Ortográfico`
                    : html`<svg class="error-icon" viewBox="0 0 24 24">
                          <path
                            d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm5-9h2v4h-2v-4zm0-6h2v2h-2V5z"
                          />
                        </svg>
                        Erro Gramatical`}
                </h3>
              </div>
              <div class="error-word">"${error.word}"</div>
              <p><strong>Contexto:</strong> "...${error.context}..."</p>
              <ul class="suggestions-list">
                ${error.suggestions.map(
                  suggestion => html`
                    <li>
                      <button
                        class="suggestion-button"
                        @click=${() => this._applySuggestion(suggestion, error)}
                      >
                        ${suggestion}
                      </button>
                    </li>
                  `,
                )}
              </ul>
            </div>
          `,
        )}
      </div>
    `;
  }

  private _getErrorContext(word: string, start: number): string {
    if (!this.editor) return '';

    const text = this.editor.getText();
    const contextWindow = 20;
    const contextStart = Math.max(0, start - contextWindow);
    const contextEnd = Math.min(
      text.length,
      start + word.length + contextWindow,
    );

    return text.slice(contextStart, contextEnd);
  }

  private _findWordPosition(word: string, startFrom: number = 0): number {
    if (!this.editor) return -1;
    const text = this.editor.getText();

    // Procura a próxima ocorrência da palavra a partir da posição startFrom
    return text.indexOf(word, startFrom);
  }

  private static _checkOverlap(
    start: number,
    end: number,
    existingErrors: TextError[],
  ): boolean {
    return existingErrors.some(
      error =>
        (start >= error.start && start < error.end) || // Início dentro de outro erro
        (end > error.start && end <= error.end) || // Fim dentro de outro erro
        (start <= error.start && end >= error.end), // Erro completamente contido
    );
  }

  private async _checkText(fromCorrection: boolean = false) {
    if (this.isLoading) return;

    // Dispatch click_correct event
    this.dispatchEvent(
      new CustomEvent('click_correct', {
        bubbles: true,
        composed: true,
      }),
    );

    // Verificar limite diário
    if (
      !this.isPremium &&
      !UsageTrackingService.checkDailyUsage(fromCorrection)
    ) {
      this.showUsageLimitError = true;
      setTimeout(() => {
        this.showUsageLimitError = false;
      }, 3000);
      return;
    }

    if (!this.editor) return;

    const currentText = this.editor.getText();

    // Verifica se o texto é o mesmo da última verificação
    if (currentText === this.lastCheckedText && !fromCorrection) {
      return;
    }

    if (this.charCount > SgSpellChecker.MAX_CHARS && !this.isPremium) {
      this.showCountWarning = true;
      this.showEditorWarning = true;
      setTimeout(() => {
        this.showCountWarning = false;
        this.showEditorWarning = false;
      }, 1000);
      return;
    }

    this.isLoading = true;
    this.hasChecked = true;
    const originalText = this.editor.getText();

    try {
      const errors = await this.spellCheckerService.checkText(
        originalText,
        this.language,
        fromCorrection, // recheck será true apenas quando vier de uma correção
      );

      this.editor.commands.unsetAllMarks();
      const lastPositionMap = new Map<string, number>();
      const processedErrors: TextError[] = [];

      const preferedErrorSource = this.preferAi ? 'ai' : 'languageTools';

      // Primeiro processa os erros do LanguageTools
      errors
        .filter(error => error.source === preferedErrorSource)
        .forEach(originalError => {
          const error = { ...originalError };
          const wordAtPosition = originalText.slice(error.start, error.end);
          if (wordAtPosition === error.word) {
            processedErrors.push(error);
          } else {
            const lastPos = lastPositionMap.get(error.word) || 0;
            const newPosition = this._findWordPosition(error.word, lastPos);

            if (
              newPosition !== -1 &&
              !SgSpellChecker._checkOverlap(
                newPosition,
                newPosition + error.word.length,
                processedErrors,
              )
            ) {
              error.start = newPosition;
              error.end = newPosition + error.word.length;
              lastPositionMap.set(error.word, newPosition + 1);
              processedErrors.push(error);
            }
          }
        });

      // Depois processa os outros erros, evitando sobreposições
      errors
        .filter(error => error.source !== preferedErrorSource)
        .forEach(originalError => {
          const error = { ...originalError };
          const wordAtPosition = originalText.slice(error.start, error.end);
          if (
            wordAtPosition === error.word &&
            !SgSpellChecker._checkOverlap(
              error.start,
              error.end,
              processedErrors,
            )
          ) {
            processedErrors.push(error);
          } else {
            const lastPos = lastPositionMap.get(error.word) || 0;
            const newPosition = this._findWordPosition(error.word, lastPos);

            if (
              newPosition !== -1 &&
              !SgSpellChecker._checkOverlap(
                newPosition,
                newPosition + error.word.length,
                processedErrors,
              )
            ) {
              error.start = newPosition;
              error.end = newPosition + error.word.length;
              lastPositionMap.set(error.word, newPosition + 1);
              processedErrors.push(error);
            }
          }
        });

      this.errors = processedErrors.map(error => ({
        ...error,
        context: this._getErrorContext(error.word, error.start),
      }));

      // Atualiza o estado hasCheckedWithNoErrors
      this.hasCheckedWithNoErrors = this.hasChecked && this.errors.length === 0;

      // Mark each valid error in the editor
      this.errors.forEach(error => {
        this._markErrorInEditor(error);
      });

      // Atualiza o último texto verificado
      this.lastCheckedText = currentText;
    } catch (error) {
      console.error('Error checking text:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private _markErrorInEditor(error: TextError) {
    if (!this.editor) return;

    try {
      const from = error.start + 1; // +1 for paragraph node
      const to = error.end + 1;

      this.editor
        .chain()
        .setTextSelection({ from, to })
        .setMark(error.type === 'spelling' ? 'spellingError' : 'grammarError', {
          source: error.source,
        })
        .run();
    } catch (e) {
      console.error('Error marking text:', e);
    }
  }

  private _applySuggestion(suggestion: string, error: TextError) {
    if (!this.editor) return;

    const from = error.start + 1;
    const to = error.end + 1;

    // First remove the mark
    this.editor
      .chain()
      .setTextSelection({ from, to })
      .unsetMark('spellingError')
      .unsetMark('grammarError')
      .run();

    // Then replace the text
    this.editor.commands.setTextSelection({ from, to });
    this.editor.commands.insertContent(suggestion);

    // Close any open hover card
    this.activeHoverCard = null;

    // Calculate the length difference after replacement
    const lengthDiff = suggestion.length - (error.end - error.start);

    // Remove the applied error and adjust positions of remaining errors
    this.errors = this.errors.filter(e => {
      if (e === error) return false;

      // If the error comes after the replaced text, adjust its position
      if (e.start > error.start) {
        e.start += lengthDiff;
        e.end += lengthDiff;
      }
      return true;
    });

    // Verificar o texto novamente após aplicar a correção
    this._checkText(true); // fromCorrection = true
  }

  private _applyAllCorrections() {
    if (!this.editor) return;

    // Sort errors by position in descending order to avoid position shifts
    const sortedErrors = [...this.errors].sort((a, b) => b.start - a.start);

    // Apply corrections one by one from end to start
    for (const error of sortedErrors) {
      const from = error.start + 1;
      const to = error.end + 1;

      this.editor
        .chain()
        .setTextSelection({ from, to })
        .unsetMark('spellingError')
        .unsetMark('grammarError')
        .run();

      this.editor.commands.setTextSelection({ from, to });
      this.editor.commands.insertContent(error.suggestions[0]);
    }

    // Clear any remaining errors
    this.errors = [];

    // Verificar o texto novamente após aplicar todas as correções
    this._checkText(true); // fromCorrection = true
  }

  private _handleEditorClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    if (target.closest('.spelling-error, .grammar-error')) {
      const word = target.textContent || '';
      const targetRect = target.getBoundingClientRect();
      const editorElement = this.shadowRoot?.querySelector('#editor');
      const editorRect = editorElement?.getBoundingClientRect();
      const isMobile = window.innerWidth <= 768;

      // Get the exact position in the editor
      const pos = this.editor?.view.posAtDOM(target, 0);

      // Find the error that matches both the word and position
      const error = this.errors.find(
        err =>
          err.word === word &&
          pos &&
          pos >= err.start + 1 &&
          pos <= err.end + 1,
      );

      if (error && editorRect) {
        if (isMobile) {
          // Em mobile, centraliza horizontalmente e posiciona abaixo da palavra relativo ao editor
          this.activeHoverCard = {
            x: editorRect.width / 2,
            y: targetRect.bottom - editorRect.top,
            error,
          };
        } else {
          // Cálculo normal para desktop
          const componentRect = this.getBoundingClientRect();
          this.activeHoverCard = {
            x: targetRect.left - componentRect.left,
            y: targetRect.bottom - componentRect.top,
            error,
          };
        }
      }
    } else if (!target.closest('.hover-card')) {
      this.activeHoverCard = null;
    }
  }

  private _renderHoverCard() {
    if (!this.activeHoverCard) return '';

    const { error, x, y } = this.activeHoverCard;

    // Add positioning logic to prevent overflow
    const hoverCard = this.shadowRoot?.querySelector(
      '.hover-card',
    ) as HTMLElement;
    const componentRect = this.getBoundingClientRect();

    let adjustedX = x;
    let adjustedY = y + 8; // Add small offset from the word

    // After the hover card is rendered, adjust its position if it overflows
    if (hoverCard) {
      const hoverCardRect = hoverCard.getBoundingClientRect();

      // Adjust horizontal position if it overflows right
      if (x + hoverCardRect.width > componentRect.width) {
        adjustedX = componentRect.width - hoverCardRect.width;
      }

      // Adjust vertical position if it overflows bottom
      if (y + hoverCardRect.height > componentRect.height) {
        // Position above the word instead
        adjustedY = y - hoverCardRect.height - 16;
      }
    }

    return html`
      <div
        class="hover-card"
        style="left: ${adjustedX}px; top: ${adjustedY}px;"
      >
        <div class="hover-card-header">
          <h3 class="hover-card-title">
            ${error.type === 'spelling'
              ? html`<svg class="error-icon" viewBox="0 0 24 24">
                    <path
                      d="M12.45 16h2.09L9.43 3H7.57L2.46 16h2.09l1.12-3h5.64l1.14 3zm-6.02-5L8.5 5.48 10.57 11H6.43zm15.16.59l-8.09 8.09L9.83 16l-1.41 1.41 5.09 5.09L23 13l-1.41-1.41z"
                    />
                  </svg>
                  Erro Ortográfico`
              : html`<svg class="error-icon" viewBox="0 0 24 24">
                    <path
                      d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm5-9h2v4h-2v-4zm0-6h2v2h-2V5z"
                    />
                  </svg>
                  Erro Gramatical`}
          </h3>
        </div>
        <div class="hover-card-content">${error.explanation}</div>
        <div class="hover-card-suggestion">
          <ul class="suggestions-list">
            ${error.suggestions.map(
              suggestion => html`
                <li>
                  <button
                    class="suggestion-button"
                    @click=${() => this._applySuggestion(suggestion, error)}
                  >
                    ${suggestion}
                  </button>
                </li>
              `,
            )}
          </ul>
        </div>
      </div>
    `;
  }

  private _renderErrorCounts() {
    const spellingErrors = this.errors.filter(
      e => e.type === 'spelling',
    ).length;
    const grammarErrors = this.errors.filter(e => e.type === 'grammar').length;

    return html`
      <div class="error-counts">
        <div class="error-count spelling">
          <span class="count">${spellingErrors}</span>
          <span class="label">Erros Ortográficos</span>
        </div>
        <div class="error-count grammar">
          <span class="count">${grammarErrors}</span>
          <span class="label">Erros Gramaticais</span>
        </div>
      </div>
    `;
  }

  private async _copyText() {
    if (!this.editor) return;

    const text = this.editor.getText();

    try {
      await navigator.clipboard.writeText(text);
      this.showCopyFeedback = true;

      // Foca o editor e depois seleciona todo o texto
      this.editor.commands.focus();
      this.editor.commands.selectAll();

      setTimeout(() => {
        this.showCopyFeedback = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  }

  private async _handlePaste(e?: ClipboardEvent) {
    let text: string | undefined;

    if (e) {
      e.preventDefault();
      text = e.clipboardData?.getData('text/plain');
    } else {
      text = await navigator.clipboard.readText();
    }

    if (this.editor && text) {
      this.editor.commands.insertContent(text);
      setTimeout(() => {
        this._checkText();
      }, 100);
    }
  }

  private async _pasteText() {
    try {
      await this._handlePaste();
    } catch (err) {
      console.error('Failed to paste text:', err);
    }
  }

  private _clearText() {
    if (!this.editor) return;

    // Limpa o conteúdo do editor
    this.editor.commands.clearContent();

    // Reset todos os estados para o inicial
    this.isEditorEmpty = true;
    this.errors = [];
    this.hasChecked = false;
    this.activeHoverCard = null;
    this.showDiff = false;
    this.showDiffView = false;
    this.originalText = '';
    this.rewrittenText = '';
    this.lastCheckedText = '';
    this.charCount = 0;
    this.lastCheckedText = '';

    // Remove todas as marcações do editor
    this.editor.commands.unsetAllMarks();
  }

  private async _rewriteText() {
    if (!this.editor || this.isRewriting) return;

    if (this.charCount > SgSpellChecker.MAX_CHARS) {
      this.showCountWarning = true;
      this.showEditorWarning = true;
      setTimeout(() => {
        this.showCountWarning = false;
        this.showEditorWarning = false;
      }, 1000);
      return;
    }

    this.isRewriting = true;
    this.originalText = this.editor.getText();

    try {
      this.rewrittenText = await this.spellCheckerService.rewriteText(
        this.originalText,
        this.language,
      );

      this.showDiff = true;
      this.showDiffView = false;
      this.editor.commands.setContent(this.rewrittenText);
      this.errors = [];
      this.hasChecked = true;
    } catch (err) {
      console.error('Failed to rewrite text:', err);
    } finally {
      this.isRewriting = false;
    }
  }

  private _renderDiff() {
    return html`
      <div class="diff-container">
        <div class="diff-controls">
          <button
            class="icon-text-button"
            @click=${() => {
              this.showDiffView = !this.showDiffView;
            }}
          >
            <svg viewBox="0 0 24 24">
              <path
                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
              />
            </svg>
            ${this.showDiffView ? 'Ocultar diferenças' : 'Mostrar diferenças'}
          </button>
          <button
            class="icon-text-button"
            @mousedown=${() => {
              if (this.editor) {
                this.showingOriginal = true;
                this.editor.commands.setContent(this.originalText);
              }
            }}
            @mouseup=${() => {
              if (this.editor) {
                this.showingOriginal = false;
                this.editor.commands.setContent(this.rewrittenText);
              }
            }}
            @mouseleave=${() => {
              if (this.editor && this.showingOriginal) {
                this.showingOriginal = false;
                this.editor.commands.setContent(this.rewrittenText);
              }
            }}
          >
            <svg viewBox="0 0 24 24">
              <path
                d="M12 6c3.79 0 7.17 2.13 8.82 5.5C19.17 14.87 15.79 17 12 17s-7.17-2.13-8.82-5.5C4.83 8.13 8.21 6 12 6m0-2C7 4 2.73 7.11 1 12c1.73 4.89 6 8 11 8s9.27-3.11 11-8c-1.73-4.89-6-8-11-8zm0 5c1.38 0 2.5 1.12 2.5 2.5S13.38 14 12 14s-2.5-1.12-2.5-2.5S10.62 9 12 9m0-2c-2.48 0-4.5 2.02-4.5 4.5S9.52 16 12 16s4.5-2.02 4.5-4.5S14.48 7 12 7z"
              />
            </svg>
            Mostrar original
          </button>
        </div>
        ${this.showDiffView
          ? html`
              <div class="diff-content">
                ${this.originalText.split(/\s+/).map((word, i) => {
                  const isChanged = word !== this.rewrittenText.split(/\s+/)[i];
                  return html`
                    <span class="${isChanged ? 'diff-removed' : ''}"
                      >${word}</span
                    >
                    ${isChanged
                      ? html`<span class="diff-added"
                          >${this.rewrittenText.split(/\s+/)[i] || ''}</span
                        >`
                      : nothing}
                    ${nothing}
                  `;
                })}
              </div>
            `
          : ''}
      </div>
    `;
  }

  private _renderCharCount() {
    const isOverLimit = this.charCount > SgSpellChecker.MAX_CHARS;
    const usedChecks = UsageTrackingService.getUsedChecks();
    const isUsageLimitReached =
      usedChecks >= UsageTrackingService.MAX_USES_PER_DAY;

    return html`
      <div
        class="char-count ${isOverLimit ? 'over-limit' : ''} ${this
          .showCountWarning
          ? 'warning-pulse'
          : ''}"
      >
        Caracteres: ${this.charCount}/${SgSpellChecker.MAX_CHARS}
      </div>
      <div class="usage-info">
        <div class="usage-count ${isUsageLimitReached ? 'over-limit' : ''}">
          Utilizações: ${usedChecks}/${UsageTrackingService.MAX_USES_PER_DAY}
        </div>
        ${this.hasCheckedWithNoErrors
          ? html`<span class="success-message">
              <svg viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
              Texto sem erros!
            </span>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'spell-grammar-checker': SgSpellChecker;
  }
}
